import { useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { User } from '../types/user.type';
import { AdminCreateUserRequestDTO } from '../types/dtos/admin-create-user.dto';
import { ADMIN_USERS_API_PATHS } from '../constants/users-paths.constants';

type UseAdminCreateUserResult = {
  isSubmitting: boolean;
  createUser: (payload: AdminCreateUserRequestDTO) => Promise<User | null>;
};

function isEmptyValue(fieldValue: unknown): boolean {
  return fieldValue === '' || fieldValue === null || fieldValue === undefined;
}

function omitEmptyOptionalFields(payload: AdminCreateUserRequestDTO): Partial<AdminCreateUserRequestDTO> {
  const nonEmptyEntries = Object.entries(payload).filter(([, fieldValue]) => !isEmptyValue(fieldValue));

  return Object.fromEntries(nonEmptyEntries) as Partial<AdminCreateUserRequestDTO>;
}

export function useAdminCreateUser(): UseAdminCreateUserResult {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createUser = async (payload: AdminCreateUserRequestDTO): Promise<User | null> => {
    setIsSubmitting(true);
    try {
      const createdUser = await HttpClient.post<User, Partial<AdminCreateUserRequestDTO>>(
        ADMIN_USERS_API_PATHS.CREATE_USER,
        omitEmptyOptionalFields(payload)
      );

      enqueueSnackbar('Utilisateur créé avec succès', { variant: 'success' });
      return createdUser;
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, "Une erreur est survenue lors de la création de l'utilisateur"),
        { variant: 'error' }
      );
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, createUser };
}
